import React, { useEffect, useRef, useState } from "react";
import { Loader2, Upload, X, Save, AlertTriangle, Image as ImageIcon, UploadCloud, CheckCircle2 } from "lucide-react";
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import SortableGalleryItem from "@/components/admin/SortableGalleryItem";
import api, { readError } from "@/lib/api";

const LOCALES = ["hr", "en", "de", "fr"];
const LOC_LABELS = { hr: "Hrvatski", en: "English", de: "Deutsch", fr: "Français" };
const CATEGORIES = ["workshop", "instruments", "restoration"];
const MAX_UPLOAD_BYTES = 15 * 1024 * 1024; // 15 MB per file — matches brief

export default function AdminGallery() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // gallery item or null
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Bulk upload state: array of { name, size, status, progress, error, url }
  const [batch, setBatch] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const bulkInputRef = useRef(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = async () => {
    setError("");
    try {
      const { data } = await api.get("/admin/gallery");
      setItems(data);
    } catch (err) {
      setError(readError(err, "Could not load gallery."));
    }
  };

  useEffect(() => { load(); }, []);

  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((i) => i.id === active.id);
    const newIdx = items.findIndex((i) => i.id === over.id);
    const next = arrayMove(items, oldIdx, newIdx);
    setItems(next);
    try {
      await api.post("/admin/gallery/reorder", { order: next.map((i) => i.id) });
      toast.success("Order saved.");
    } catch (err) {
      toast.error(readError(err, "Reorder failed."));
      await load();
    }
  };

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: u } = await api.post("/admin/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
      // Open the editor pre-filled with the new image so the user can add captions
      setEditing({
        url: u.url,
        category: "workshop",
        caption: { hr: "", en: "", de: "", fr: "" },
        visible: true,
        sort_order: (items?.length || 0) * 10 + 10,
        isNew: true,
      });
      toast.success(`Uploaded ${u.width}×${u.height} (${u.size_kb} KB) — add captions and save.`);
    } catch (err) {
      toast.error(readError(err, "Upload failed."));
    } finally {
      setUploading(false);
    }
  };

  // ---------- Bulk upload ----------
  const startBulkUpload = async (files) => {
    const arr = Array.from(files || []).filter((f) => f && f.type?.startsWith("image/"));
    if (arr.length === 0) {
      toast.error("No image files selected.");
      return;
    }
    // Build state entries
    const entries = arr.map((f, i) => ({
      key: `${Date.now()}-${i}-${f.name}`,
      name: f.name,
      size: f.size,
      file: f,
      status: f.size > MAX_UPLOAD_BYTES ? "error" : "queued",
      progress: 0,
      error: f.size > MAX_UPLOAD_BYTES ? `Exceeds 15 MB limit (${(f.size / 1024 / 1024).toFixed(1)} MB)` : "",
      url: "",
    }));
    setBatch(entries);

    // Sequential upload (keeps backend + browser calm; per-file progress via axios onUploadProgress)
    let baseSort = (items?.length || 0) * 10 + 10;
    let successCount = 0;
    for (const entry of entries) {
      if (entry.status === "error") continue;
      setBatch((prev) => prev.map((b) => (b.key === entry.key ? { ...b, status: "uploading" } : b)));
      try {
        const form = new FormData();
        form.append("file", entry.file);
        const { data: u } = await api.post("/admin/uploads", form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            const pct = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
            setBatch((prev) => prev.map((b) => (b.key === entry.key ? { ...b, progress: pct } : b)));
          },
        });
        // Create the gallery record with default (empty) captions at the end of the list
        await api.post("/admin/gallery", {
          url: u.url,
          category: "workshop",
          caption: { hr: "", en: "", de: "", fr: "" },
          visible: true,
          sort_order: baseSort,
        });
        baseSort += 10;
        successCount += 1;
        setBatch((prev) => prev.map((b) => (b.key === entry.key ? { ...b, status: "done", progress: 100, url: u.url } : b)));
      } catch (err) {
        setBatch((prev) =>
          prev.map((b) => (b.key === entry.key ? { ...b, status: "error", error: readError(err, "Upload failed") } : b))
        );
      }
    }
    if (successCount > 0) {
      toast.success(`${successCount} slika uspješno prenesene / ${successCount} photo${successCount === 1 ? "" : "s"} uploaded successfully`);
      await load();
    } else {
      toast.error("No files uploaded successfully — check per-file errors below.");
    }
  };

  const onBulkInputChange = (e) => {
    startBulkUpload(e.target.files);
    if (bulkInputRef.current) bulkInputRef.current.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) startBulkUpload(e.dataTransfer.files);
  };
  const onDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };
  const clearBatch = () => setBatch([]);

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      const payload = { ...editing };
      if (editing.isNew) {
        delete payload.isNew;
        await api.post("/admin/gallery", payload);
        toast.success("Gallery item added.");
      } else {
        await api.put(`/admin/gallery/${editing.id}`, payload);
        toast.success("Gallery item saved.");
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(readError(err, "Save failed."));
    } finally {
      setBusy(false);
    }
  };

  const del = async (item) => {
    if (!window.confirm(`Delete this gallery item? (${item.id})`)) return;
    try {
      await api.delete(`/admin/gallery/${item.id}`);
      toast.success("Deleted.");
      await load();
    } catch (err) {
      toast.error(readError(err, "Delete failed."));
    }
  };

  const toggleVisible = async (item) => {
    try {
      await api.put(`/admin/gallery/${item.id}`, { ...item, visible: !item.visible });
      await load();
    } catch (err) {
      toast.error(readError(err, "Toggle failed."));
    }
  };

  return (
    <div className="space-y-8 animate-fade-up">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-primary">CMS</div>
          <h1 className="mt-1 font-serif text-4xl text-foreground">Gallery</h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
            Drag to reorder. Click an image to edit caption and category. Uploads are auto-converted to WebP. Drop many at once in the zone below for bulk upload.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer rounded-lg bg-primary text-primary-foreground px-4 py-2.5 text-sm shadow-interactive hover:bg-primary/90">
          <Upload className="h-4 w-4" strokeWidth={1.5} />
          {uploading ? "Uploading…" : "Upload image"}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="gallery-upload-input"
            onChange={(e) => onUpload(e.target.files?.[0])}
            disabled={uploading}
          />
        </label>
      </div>

      {/* ------- Bulk upload drop zone ------- */}
      <div
        data-testid="gallery-bulk-drop"
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onClick={() => bulkInputRef.current?.click()}
        className={`rounded-xl border-2 border-dashed transition-colors cursor-pointer p-6 sm:p-8 text-center ${
          dragActive
            ? "border-primary bg-primary/[0.06]"
            : "border-border/60 hover:border-primary/50 hover:bg-primary/[0.03] bg-card"
        }`}
      >
        <input
          ref={bulkInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          data-testid="gallery-bulk-input"
          onChange={onBulkInputChange}
        />
        <UploadCloud className={`h-8 w-8 mx-auto mb-2 ${dragActive ? "text-primary" : "text-muted-foreground/60"}`} strokeWidth={1.5} />
        <div className="text-sm font-medium text-foreground">
          {dragActive ? "Drop to upload" : "Drop multiple images here or click to select"}
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          WebP / JPG / PNG / HEIC · max 15 MB per file · captions can be added after upload
        </div>
      </div>

      {batch.length > 0 && (
        <div className="rounded-xl border border-border/60 bg-card p-4" data-testid="gallery-batch-progress">
          <div className="flex items-baseline justify-between mb-3">
            <div className="text-xs uppercase tracking-[0.22em] text-primary">Bulk upload</div>
            <button
              type="button"
              onClick={clearBatch}
              className="text-xs text-muted-foreground hover:text-foreground"
              data-testid="gallery-batch-clear"
            >
              Clear
            </button>
          </div>
          <ul className="space-y-2 max-h-64 overflow-auto">
            {batch.map((b) => (
              <li key={b.key} className="text-xs" data-testid={`bulk-item-${b.status}`}>
                <div className="flex items-center gap-2">
                  {b.status === "done" ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" strokeWidth={2} />
                  ) : b.status === "error" ? (
                    <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0" strokeWidth={2} />
                  ) : b.status === "uploading" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-primary shrink-0" strokeWidth={2} />
                  ) : (
                    <ImageIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" strokeWidth={1.5} />
                  )}
                  <span className="truncate text-foreground/85 flex-1">{b.name}</span>
                  <span className="text-muted-foreground shrink-0">{(b.size / 1024).toFixed(0)} KB</span>
                </div>
                <div className="mt-1 h-1 bg-muted rounded overflow-hidden">
                  <div
                    className={`h-full transition-all ${b.status === "error" ? "bg-destructive" : b.status === "done" ? "bg-green-600" : "bg-primary"}`}
                    style={{ width: `${b.progress || (b.status === "done" ? 100 : 0)}%` }}
                  />
                </div>
                {b.error && <div className="mt-1 text-destructive">{b.error}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5" strokeWidth={1.5} /> {error}
        </div>
      )}

      {items === null ? (
        <div className="p-10 flex items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading gallery…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground bg-card">
          <ImageIcon className="h-10 w-10 mx-auto mb-2 text-muted-foreground/40" strokeWidth={1.5} />
          No images yet — upload your first one.
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={items.map((i) => i.id)} strategy={rectSortingStrategy}>
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-5" data-testid="gallery-grid">
              {items.map((it) => (
                <SortableGalleryItem
                  key={it.id}
                  item={it}
                  onEdit={(x) => setEditing({ ...x })}
                  onDelete={del}
                  onToggleVisible={toggleVisible}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="bg-card border-border max-w-lg" data-testid="gallery-editor-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif">{editing?.isNew ? "New gallery item" : "Edit gallery item"}</DialogTitle>
            <DialogDescription>Captions are shown in the public gallery per locale.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
              <div className="rounded-lg overflow-hidden border border-border/60 bg-background">
                <img src={editing.url} alt="" className="w-full max-h-48 object-cover" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Category</Label>
                <div className="flex gap-2 flex-wrap">
                  {CATEGORIES.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setEditing((e) => ({ ...e, category: c }))}
                      data-testid={`category-${c}`}
                      className={`px-3 py-1.5 rounded-full text-xs transition-colors ${
                        editing.category === c ? "bg-primary text-primary-foreground" : "border border-border text-foreground/70 hover:text-primary"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              {LOCALES.map((loc) => (
                <div key={loc} className="space-y-1.5">
                  <Label className="text-xs">Caption · {LOC_LABELS[loc]}</Label>
                  <Textarea
                    value={editing.caption?.[loc] || ""}
                    onChange={(e) => setEditing((x) => ({ ...x, caption: { ...x.caption, [loc]: e.target.value } }))}
                    rows={2}
                    className="bg-background resize-none"
                    data-testid={`caption-${loc}`}
                  />
                </div>
              ))}
              <div className="flex items-center justify-between pt-1">
                <Label className="text-sm">Visible on public site</Label>
                <Switch checked={!!editing.visible} onCheckedChange={(v) => setEditing((x) => ({ ...x, visible: v }))} data-testid="gallery-visible" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={busy} data-testid="gallery-cancel">Cancel</Button>
            <Button onClick={save} disabled={busy} className="shadow-interactive" data-testid="gallery-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" strokeWidth={1.5} />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
